import { useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import { buildHostsUrl } from '../../buildHostsUrl';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * HomeThreePlatformOptions Component
 * Three platform option cards from AidasCopy prototype
 * Displays: Play Games From Anywhere, Earn Rewards, Upload Your Game
 * Desktop: Horizontal row of three cards with decorative imagery
 * Mobile: Stubbed for future implementation
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const titleLineStyle = (color: string): React.CSSProperties => ({
  lineHeight: '20px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '16px',
  fontStyle: 'italic',
  letterSpacing: '-0.5px',
  color,
  margin: 0,
  fontVariationSettings: "'wght' 900",
});

interface DecorativeImage {
  src: string;
  width: number;
  height: number;
  left: number;
  top: number;
  rotate?: number;
  scaleX?: number;
  shadow: string;
  zIndex?: number;
}

interface PlatformCardProps {
  title: React.ReactNode;
  bodyText: string;
  bodyWidth: number;
  backgroundSrc: string;
  buttonLabel: string;
  decorativeImages: DecorativeImage[];
  delay: number;
  isInView: boolean;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;
  buttonMessage?: string;
  bodyFontSize?: string;
  bodyLineHeight?: string;
  bodyPaddingTop?: string;
  bodyPaddingBottom?: string;
  contentShiftY?: number;
  cardExtraHeight?: number;
  contentLeft?: number;
  contentTextAlign?: 'left' | 'center' | 'right';
}

const PlatformCard = ({
  title,
  bodyText,
  bodyWidth,
  backgroundSrc,
  buttonLabel,
  decorativeImages,
  delay,
  isInView,
  onButtonClick,
  buttonDisabled,
  buttonMessage,
  bodyFontSize = '8px',
  bodyLineHeight = '11px',
  bodyPaddingTop = '0px',
  bodyPaddingBottom = '0px',
  contentShiftY = 0,
  cardExtraHeight = 0,
  contentLeft = 31,
  contentTextAlign = 'left',
}: PlatformCardProps) => (
  <motion.div
    style={{
      position: 'relative',
      width: '230px',
      height: `${278 + cardExtraHeight}px`,
    }}
    initial={{ opacity: 0, y: 20 }}
    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
    transition={{ duration: 0.4, delay, ease: 'easeOut' }}
  >
    {/* Background Blurred Image */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '31px',
        backgroundImage: `url(${backgroundSrc})`,
        backgroundPosition: '50% 50%',
        backgroundSize: 'cover',
        boxShadow: 'var(--color-accent-purple) 0px 0px 0px 1px inset, 0px -1px 12px var(--shadow-card-glow)',
        opacity: 0.5,
      }}
    />

    {/* Title */}
    <div
      style={{
        position: 'absolute',
        left: contentTextAlign === 'center' ? `${(230 - 199) / 2}px` : `${contentLeft}px`,
        top: '28px',
        width: '199px',
        zIndex: 2,
        textAlign: contentTextAlign,
      }}
    >
      {title}
    </div>

    {/* Body Text */}
    <div
      style={{
        position: 'absolute',
        left: contentTextAlign === 'center' ? `${(230 - bodyWidth) / 2}px` : `${contentLeft}px`,
        top: '72px',
        width: `${bodyWidth}px`,
        zIndex: 2,
        textAlign: contentTextAlign,
      }}
    >
      <p
        style={{
          lineHeight: bodyLineHeight,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: bodyFontSize,
          color: WHITE,
          margin: 0,
          paddingTop: bodyPaddingTop,
          paddingBottom: bodyPaddingBottom,
        }}
      >
        {bodyText}
      </p>
    </div>

    {/* Decorative Images */}
    {decorativeImages.map((img, index) => (
      <img
        key={index}
        src={img.src}
        alt=""
        style={{
          position: 'absolute',
          left: `${img.left}px`,
          top: `${img.top + contentShiftY}px`,
          width: `${img.width}px`,
          height: `${img.height}px`,
          objectFit: 'contain',
          filter: img.shadow,
          zIndex: img.zIndex ?? 3,
          ...(img.rotate !== undefined || img.scaleX !== undefined
            ? {
                transform: [
                  img.rotate !== undefined ? `rotate(${img.rotate}deg)` : '',
                  img.scaleX !== undefined ? `scaleX(${img.scaleX})` : '',
                ]
                  .filter(Boolean)
                  .join(' '),
              }
            : {}),
        }}
      />
    ))}

    {/* Disabled message above button */}
    {buttonMessage && (
      <p
        style={{
          position: 'absolute',
          left: '31px',
          top: `${220 + contentShiftY}px`,
          width: '168px',
          zIndex: 4,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '7px',
          lineHeight: '10px',
          color: 'rgba(255, 255, 255, 0.5)',
          margin: 0,
          textAlign: 'center',
        }}
      >
        {buttonMessage}
      </p>
    )}

    {/* CTA Button */}
    <button
      className="aida-button aida-button-outline-fill"
      onClick={buttonDisabled ? undefined : onButtonClick}
      disabled={buttonDisabled}
      style={{
        position: 'absolute',
        left: '45px',
        top: `${232 + contentShiftY}px`,
        width: '140px',
        height: '24px',
        borderRadius: '30px',
        zIndex: 4,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '24px',
        cursor: buttonDisabled ? 'default' : 'pointer',
        opacity: buttonDisabled ? 0.4 : 1,
        pointerEvents: buttonDisabled ? 'none' : 'auto',
      }}
    >
      <span className="text" style={{ height: '24px', lineHeight: '24px' }}>
        {buttonLabel}
      </span>
    </button>
  </motion.div>
);

interface HomeThreePlatformOptionsProps {
  activeDemoUrl?: string | null;
}

export const HomeThreePlatformOptions = ({ activeDemoUrl }: HomeThreePlatformOptionsProps) => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const navigate = useNavigate();

  const goToSteamCard = useCallback(() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Get Free Games', buttonLocation: 'HomeThreePlatformOptions' }); navigate(buildHostsUrl()); }, [navigate]);
  const goToDevOnboarding = useCallback(() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Partner with Us', buttonLocation: 'HomeThreePlatformOptions' }); navigate('/developers'); }, [navigate]);
  const goToDemo = useCallback(() => {
    if (activeDemoUrl) { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: 'Start Playing', buttonLocation: 'HomeThreePlatformOptions' }); window.open(activeDemoUrl, '_blank'); }
  }, [activeDemoUrl]);

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '40px 0',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="flex w-full justify-center"
          style={{ gap: '28px' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Card 1: Play Games From Anywhere */}
          <PlatformCard
            isInView={isInView}
            delay={0}
            bodyText="Jump into a game from anywhere in the world, with the power of Playcast."
            bodyWidth={164}
            backgroundSrc="/img/aida/HomeThreePlatformOptions/Option1/threeplatformbackground1.png"
            buttonLabel="Start Playing"
            onButtonClick={goToDemo}
            buttonDisabled={!activeDemoUrl}
            buttonMessage={!activeDemoUrl ? 'No Active Games At This Time' : undefined}
            title={
              <>
                <p style={titleLineStyle(WHITE)}>PLAY GAMES FROM</p>
                <p style={titleLineStyle(YELLOW)}>ANYWHERE</p>
              </>
            }
            decorativeImages={[
              {
                src: '/img/aida/HomeThreePlatformOptions/Option1/threeplatformphone1.png',
                left: 0,
                top: 86,
                width: 189,
                height: 140,
                shadow: 'drop-shadow(var(--shadow-image-light) 8px 6px 2px)',
              },
              {
                src: '/img/aida/HomeThreePlatformOptions/Option1/threeplatformgibber1.png',
                left: 92,
                top: 126,
                width: 76,
                height: 90,
                shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
              },
            ]}
          />

          {/* Card 2: Earn Rewards While You're Idle */}
          <PlatformCard
            isInView={isInView}
            delay={0.1}
            bodyText="Turn your idle PC into part of the Playcast edge-network."
            bodyWidth={164}
            backgroundSrc="/img/aida/HomeThreePlatformOptions/Option2/threeplatformbackground2.png"
            buttonLabel="Get Free Games"
            onButtonClick={goToSteamCard}
            title={
              <p style={titleLineStyle(WHITE)}>
                EARN <span style={{ color: YELLOW }}>REWARDS</span>
                <br />
                WHILE YOU'RE IDLE
              </p>
            }
            decorativeImages={[
              {
                src: '/img/aida/HomeThreePlatformOptions/Option2/threeplatformgibber2.png',
                left: 28.92,
                top: 92.35,
                width: 94,
                height: 135,
                rotate: -16,
                scaleX: -1,
                shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
                zIndex: 4,
              },
              {
                src: '/img/aida/HomeThreePlatformOptions/Option2/threeplatformlaptop2.png',
                left: 80,
                top: 100,
                width: 102,
                height: 121,
                shadow: 'drop-shadow(var(--shadow-image-navy-light) 8px 6px 2px)',
              },
            ]}
          />

          {/* Card 3: Upload Your Game As A Playable Link */}
          <PlatformCard
            isInView={isInView}
            delay={0.2}
            bodyText="Go beyond the trailer: let gamers try your demo in one click."
            bodyWidth={176}
            backgroundSrc="/img/aida/HomeThreePlatformOptions/Option3/threeplatformbackground3.png"
            buttonLabel="Partner with Us"
            onButtonClick={goToDevOnboarding}
            title={
              <>
                <p style={titleLineStyle(WHITE)}>UPLOAD YOUR GAME</p>
                <p style={titleLineStyle(WHITE)}>
                  AS A <span style={{ color: YELLOW }}>PLAYABLE LINK</span>
                </p>
              </>
            }
            decorativeImages={[
              {
                src: '/img/aida/HomeThreePlatformOptions/Option3/threeplatformlaptop3.png',
                left: 45,
                top: 86,
                width: 141,
                height: 140,
                shadow: 'drop-shadow(var(--shadow-image-navy) 8px 6px 2px)',
              },
              {
                src: '/img/aida/HomeThreePlatformOptions/Option3/threeplatformgibber3.png',
                left: 127,
                top: 144,
                width: 75,
                height: 82,
                shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
              },
            ]}
          />
        </motion.div>
      </motion.section>

      {/* Mobile Layout - Vertical card stack */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'visible',
          padding: '40px 0',
          gap: '24px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Card 1: Play Games From Anywhere */}
        <div style={{ width: '340px', height: '470px', position: 'relative' }}>
          <div style={{ transform: 'scale(1.478)', transformOrigin: 'top left' }}>
            <PlatformCard
              isInView={isMobileInView}
              delay={0}
              bodyFontSize="11px"
              bodyLineHeight="16px"
              bodyPaddingTop="6px"
              bodyPaddingBottom="6px"
              contentShiftY={35}
              cardExtraHeight={40}
              contentLeft={18}
              contentTextAlign="center"
              bodyText="Jump into a game from anywhere in the world, with the power of Playcast."
              bodyWidth={162}
              backgroundSrc="/img/aida/HomeThreePlatformOptions/Option1/threeplatformbackground1.png"
              buttonLabel="Start Playing"
              onButtonClick={goToDemo}
              buttonDisabled={!activeDemoUrl}
              buttonMessage={!activeDemoUrl ? 'No Active Games At This Time' : undefined}
              title={
                <>
                  <p style={titleLineStyle(WHITE)}>PLAY GAMES FROM</p>
                  <p style={titleLineStyle(YELLOW)}>ANYWHERE</p>
                </>
              }
              decorativeImages={[
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option1/threeplatformphone1.png',
                  left: 0,
                  top: 86,
                  width: 189,
                  height: 140,
                  shadow: 'drop-shadow(var(--shadow-image-light) 8px 6px 2px)',
                },
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option1/threeplatformgibber1.png',
                  left: 92,
                  top: 126,
                  width: 76,
                  height: 90,
                  shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
                },
              ]}
            />
          </div>
        </div>

        {/* Card 2: Earn Rewards While You're Idle */}
        <div style={{ width: '340px', height: '470px', position: 'relative' }}>
          <div style={{ transform: 'scale(1.478)', transformOrigin: 'top left' }}>
            <PlatformCard
              isInView={isMobileInView}
              delay={0.1}
              bodyFontSize="11px"
              bodyLineHeight="16px"
              bodyPaddingTop="6px"
              bodyPaddingBottom="6px"
              contentShiftY={35}
              cardExtraHeight={40}
              contentLeft={18}
              contentTextAlign="center"
              bodyText="Turn your idle PC into part of the Playcast edge-network."
              bodyWidth={162}
              backgroundSrc="/img/aida/HomeThreePlatformOptions/Option2/threeplatformbackground2.png"
              buttonLabel="Get Free Games"
              onButtonClick={goToSteamCard}
              title={
                <p style={titleLineStyle(WHITE)}>
                  EARN <span style={{ color: YELLOW }}>REWARDS</span>
                  <br />
                  WHILE YOU&apos;RE IDLE
                </p>
              }
              decorativeImages={[
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option2/threeplatformgibber2.png',
                  left: 28.92,
                  top: 92.35,
                  width: 94,
                  height: 135,
                  rotate: -16,
                  scaleX: -1,
                  shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
                  zIndex: 4,
                },
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option2/threeplatformlaptop2.png',
                  left: 80,
                  top: 100,
                  width: 102,
                  height: 121,
                  shadow: 'drop-shadow(var(--shadow-image-navy-light) 8px 6px 2px)',
                },
              ]}
            />
          </div>
        </div>

        {/* Card 3: Upload Your Game As A Playable Link */}
        <div style={{ width: '340px', height: '470px', position: 'relative' }}>
          <div style={{ transform: 'scale(1.478)', transformOrigin: 'top left' }}>
            <PlatformCard
              isInView={isMobileInView}
              delay={0.2}
              bodyFontSize="11px"
              bodyLineHeight="16px"
              bodyPaddingTop="6px"
              bodyPaddingBottom="6px"
              contentShiftY={35}
              cardExtraHeight={40}
              contentLeft={18}
              contentTextAlign="center"
              bodyText="Go beyond the trailer: let gamers try your demo in one click."
              bodyWidth={174}
              backgroundSrc="/img/aida/HomeThreePlatformOptions/Option3/threeplatformbackground3.png"
              buttonLabel="Partner with Us"
              onButtonClick={goToDevOnboarding}
              title={
                <>
                  <p style={titleLineStyle(WHITE)}>UPLOAD YOUR GAME</p>
                  <p style={titleLineStyle(WHITE)}>
                    AS A <span style={{ color: YELLOW }}>PLAYABLE LINK</span>
                  </p>
                </>
              }
              decorativeImages={[
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option3/threeplatformlaptop3.png',
                  left: 45,
                  top: 86,
                  width: 141,
                  height: 140,
                  shadow: 'drop-shadow(var(--shadow-image-navy) 8px 6px 2px)',
                },
                {
                  src: '/img/aida/HomeThreePlatformOptions/Option3/threeplatformgibber3.png',
                  left: 127,
                  top: 144,
                  width: 75,
                  height: 82,
                  shadow: 'drop-shadow(var(--shadow-image-medium) 1px 2px 7px)',
                },
              ]}
            />
          </div>
        </div>
      </motion.section>
    </>
  );
};
